import { useState, useCallback, useMemo } from 'react';
import { FINANCE_CONSTANTS } from '@constants/carFinance';
import getIndianCurrencyFormat from '@helpers/getIndianCurrencyFormat';
import { calculateDefaultDownPayment, parseInterest } from '@helpers/carFinanceCalculations';
import type { 
  BankData, 
  SavingsBreakupData, 
  FinanceState, 
  ModalState, 
  InputState, 
  TempState,
  UseCarFinanceStateProps 
} from '@interfaces/CarFinance.types';

export const useCarFinanceState = ({
  onRoadPrice,
  savingsBreakupData,
  initialSelectedBank,
}: UseCarFinanceStateProps) => {
  // Calculate default down payment using helper function
  const defaultDownPayment = useMemo(() => 
    calculateDefaultDownPayment(onRoadPrice, savingsBreakupData),
    [onRoadPrice, savingsBreakupData]
  );

  // Initial state
  const initialFinanceState: FinanceState = {
    downPayment: defaultDownPayment,
    loanTenure: FINANCE_CONSTANTS.INITIAL_DURATION,
    selectedBank: initialSelectedBank,
    otherInterest: parseInterest(initialSelectedBank?.interest_rate),
    carLoanDiscount: 0,
    iteration: 0,
    isInitial: true,
    checkChangeDownpayment: false,
  };

  const initialModalState: ModalState = {
    isOpen: false,
    type: null,
    editMode: undefined,
  };

  const initialInputState: InputState = {
    downPaymentInputValue: getIndianCurrencyFormat(defaultDownPayment),
    downPaymentError: "",
    interestInputValue: parseInterest(initialSelectedBank?.interest_rate) !== "" 
      ? String(parseInterest(initialSelectedBank?.interest_rate)) 
      : "",
    interestError: "",
  };

  const initialTempState: TempState = {
    tempLoanTenure: FINANCE_CONSTANTS.INITIAL_DURATION,
    tempSelectedBank: initialSelectedBank,
    tempOtherInterest: parseInterest(initialSelectedBank?.interest_rate),
  };

  // State hooks
  const [financeState, setFinanceState] = useState<FinanceState>(initialFinanceState);
  const [modalState, setModalState] = useState<ModalState>(initialModalState);
  const [inputState, setInputState] = useState<InputState>(initialInputState);
  const [tempState, setTempState] = useState<TempState>(initialTempState);

  // State update functions
  const updateFinanceState = useCallback((updates: Partial<FinanceState>) => {
    setFinanceState(prev => ({ ...prev, ...updates }));
  }, []);

  const updateModalState = useCallback((updates: Partial<ModalState>) => {
    setModalState(prev => ({ ...prev, ...updates }));
  }, []);

  const updateInputState = useCallback((updates: Partial<InputState>) => {
    setInputState(prev => ({ ...prev, ...updates }));
  }, []);

  const updateTempState = useCallback((updates: Partial<TempState>) => {
    setTempState(prev => ({ ...prev, ...updates }));
  }, []);

  // Specific state handlers
  const openModal = useCallback((type: "downpayment" | "tenure" | "interest") => {
    updateModalState({
      isOpen: true,
      type,
      editMode: type,
    });
  }, [updateModalState]);

  const closeModal = useCallback(() => {
    updateModalState({
      isOpen: false,
      type: null,
      editMode: undefined,
    });
  }, [updateModalState]);

  const updateLoanTenure = useCallback((value: number) => {
    updateFinanceState({ loanTenure: value });
    updateTempState({ tempLoanTenure: value });
  }, [updateFinanceState, updateTempState]);

  const updateSelectedBank = useCallback((bank: BankData) => {
    updateFinanceState({
      selectedBank: bank,
      otherInterest: bank?.interest_rate,
    });
    updateInputState({
      interestInputValue: String(bank?.interest_rate || ""),
      interestError: "",
    });
  }, [updateFinanceState, updateInputState]);

  const updateOtherInterest = useCallback((value: number | string) => {
    updateFinanceState({ otherInterest: value });
  }, [updateFinanceState]);

  const updateCarLoanDiscount = useCallback((value: number) => {
    setFinanceState(prev => ({
      ...prev,
      carLoanDiscount: value,
      iteration: prev.iteration + 1,
      isInitial: prev.iteration === 0 ? false : prev.isInitial,
    }));
  }, []);

  return {
    // State
    financeState,
    modalState,
    inputState,
    tempState,
    
    // State updaters
    updateFinanceState,
    updateModalState,
    updateInputState,
    updateTempState,
    
    // Specific handlers
    openModal,
    closeModal,
    updateLoanTenure,
    updateSelectedBank,
    updateOtherInterest,
    updateCarLoanDiscount,
    
    // Helpers
    parseInterest,
  };
};
